import React, { memo, useCallback, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

import RegionsSearch from 'components/regions/RegionsSearch';
import { toLookup } from 'utils/data';
import { useUpdateEffect } from 'hooks/useUpdateEffect';

/**
 * Uncontrolled component for selecting multiple regions (displayed as Chips)
 * Accept default region IDs list and onChange callback
 * @note in readonly mode Chips are only shown if multiple regions
 */
function RegionsMultiSelect({ regionIds, onChange, readonly, error }) {
  const regionRef = useRef(null);
  const [regionsDict, setRegionsDict] = useState(() => toLookup(regionIds));
  const defaultSelectedRegionId = useMemo(() => regionIds.at(-1), [regionIds]);

  useUpdateEffect(() => {
    onChange(Object.keys(regionsDict));
  }, [regionsDict, onChange]);

  const addRegion = useCallback(() => {
    const currentRegionId = regionRef.current?.value;
    if (!currentRegionId) return;
    setRegionsDict(prev => ({ ...prev, [currentRegionId]: true }));
  }, [regionRef, setRegionsDict]);

  const deleteRegion = useCallback(
    regionId => {
      if (!regionId) return;
      setRegionsDict(prev => {
        const next = { ...prev };
        delete next[regionId];
        return next;
      });
    },
    [setRegionsDict],
  );

  const isSingleRegion = (regionIds?.length ?? 0) === 1;
  const hideChips = isSingleRegion && readonly;

  return (
    <Box>
      <Box sx={{ display: 'flex', marginBottom: '8px' }}>
        <RegionsSearch
          inputRef={regionRef}
          defaultValue={defaultSelectedRegionId ?? null}
          textFieldProps={{ label: 'Region' }}
          readonly={readonly}
          error={error && !readonly}
        />
        &nbsp;
        <Button
          disabled={readonly}
          onClick={addRegion}
          className="add-button"
          variant="outlined"
          color="primary"
        >
          +
        </Button>
      </Box>
      {!hideChips && (
        <Box sx={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
          {Object.keys(regionsDict).map(regionId => (
            <Chip
              key={regionId}
              label={regionId}
              color="primary"
              variant="outlined"
              onDelete={readonly ? undefined : () => deleteRegion(regionId)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

RegionsMultiSelect.propTypes = {
  regionIds: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  readonly: PropTypes.bool,
  error: PropTypes.bool,
};

RegionsMultiSelect.defaultProps = {
  regionIds: [],
  readonly: false,
  error: false,
};

export default memo(RegionsMultiSelect);
